import { memo } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Heart, Briefcase, Star, Notebook, Pencil } from 'lucide-react';

const CalendarDay = ({
    dayData,
    isSelected,
    isAnySelected,
    isManualMode = false,
    onClick,
    onLongPressStart,
    onLongPressEnd,
    getDayStyle,
    getApStatusForDay,
    shiftOverrides = {}
}) => {
    if (dayData.empty) {
        // En móvil, los días vacíos también ocupan espacio en el grid
        return <div className="calendar-day-fill md:min-h-[55px]" />;
    }

    const dayStyle = getDayStyle(dayData);
    const isStyleObject = typeof dayStyle === 'object' && dayStyle !== null;
    const apStatus = getApStatusForDay(dayData.dateStr);

    // DETERMINAR SI MOSTRAR EL LÁPIZ DE EDICIÓN
    // En modo MANUAL, el lápiz solo se muestra si hay un motivo explícito (AP, Baja, etc.)
    // Pero NO por las asignaciones de turno que vienen de pintar el calendario.
    const showPencil = dayData.motivo?.motivo_id || (!isManualMode && shiftOverrides[dayData.dateStr]);

    return (
        <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onClick(dayData)}
            onMouseDown={() => onLongPressStart(dayData)}
            onMouseUp={onLongPressEnd}
            onMouseLeave={onLongPressEnd}
            onTouchStart={() => onLongPressStart(dayData)}
            onTouchEnd={onLongPressEnd}
            onTouchMove={onLongPressEnd}
            onContextMenu={(e) => e.preventDefault()}
            className={`
                ${isStyleObject ? dayStyle.className : dayStyle}
                ${dayData.isToday && !isSelected ? 'ring-1 ring-[var(--text-primary)] ring-offset-1 ring-offset-[var(--bg-primary)]' : ''}
                ${isSelected
                    ? 'scale-[1.15] z-50 shadow-[0_20px_50px_rgba(0,0,0,0.6)] ring-2 ring-white ring-offset-2 ring-offset-[var(--bg-primary)] brightness-125 !rounded-xl'
                    : isAnySelected
                        ? 'opacity-30 scale-95 grayscale-[0.5] blur-[0.5px]'
                        : 'hover:brightness-110'
                }
                ${dayData.isPainterInRange ? 'ring-2 ring-cyan-400/50 shadow-[0_0_15px_rgba(34,211,238,0.3)] z-10' : ''}
                ${(dayData.isPainterStart || dayData.isPainterEnd) ? '!brightness-125 ring-2 !ring-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.5)] z-20' : ''}
                calendar-day-fill md:min-h-[55px] flex flex-col items-center justify-center relative rounded-lg md:rounded-xl transition-all duration-500 cubic-bezier(0.25, 0.46, 0.45, 0.94) select-none !touch-manipulation outline-none
            `}
            style={isStyleObject ? dayStyle.style : {}}
        >
            {/* Yellow ribbon for festivos/domingos (TOP-RIGHT) */}
            {(dayData.isFestivo || dayData.isSunday) && (
                <div className="absolute top-0 right-0 w-0 h-0 border-t-[14px] md:border-t-[16px] border-t-yellow-400 border-l-[14px] md:border-l-[16px] border-l-transparent z-10 rounded-tr-lg md:rounded-tr-xl shadow-sm" />
            )}

            {/* White ribbon for partial holidays - TOP-LEFT */}
            {dayData.isPartialHoliday && (
                <div className="absolute top-0 left-0 w-0 h-0 border-t-[14px] md:border-t-[16px] border-t-white border-r-[14px] md:border-r-[16px] border-r-transparent z-10 rounded-tl-lg md:rounded-tl-xl shadow-sm" />
            )}

            {/* Ribbon para Anexos - BOTTOM-LEFT: color según tipo */}
            {dayData.isAnexo && (
                <div className={`absolute bottom-0 left-0 w-0 h-0 border-b-[14px] md:border-b-[16px] ${dayData.isFiestaCompensada ? 'border-b-purple-500' :
                    dayData.isCompAnosAnt ? 'border-b-amber-500' :
                        'border-b-orange-500'
                    } border-r-[14px] md:border-r-[16px] border-r-transparent z-10 rounded-bl-lg md:rounded-bl-xl shadow-sm`} />
            )}

            {/* Etiqueta tipo especial: FC o CA */}
            {dayData.isFiestaCompensada && (
                <span className="absolute bottom-0.5 right-0.5 md:bottom-1 md:right-1 text-[7px] md:text-[9px] font-black text-white/80 z-20">FC</span>
            )}
            {dayData.isCompAnosAnt && (
                <span className="absolute bottom-0.5 right-0.5 md:bottom-1 md:right-1 text-[7px] md:text-[9px] font-black text-white/80 z-20">CA</span>
            )}

            {/* Modification indicator (Pencil) */}
            {showPencil && (
                <div className="absolute top-1 left-1 md:top-1.5 md:left-1.5 z-20 flex items-center justify-center w-3.5 h-3.5 md:w-4 md:h-4 bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.8)] border border-white/50">
                    <Pencil className="w-2 h-2 md:w-2.5 md:h-2.5 text-gray-900" />
                </div>
            )}

            <span className={`text-sm md:text-lg font-black ${isSelected ? 'scale-110' : ''}`}>{dayData.day}</span>

            {dayData.turno && (!dayData.isLibranza || dayData.turno === 'CF') && !dayData.isVacacion && (!dayData.motivo || !dayData.motivo.motivo_id || (dayData.motivo?.tipo_hora_sindical === 'reunion_sindical' || dayData.motivo?.tipo_hora_sindical === 'asamblea')) && (
                <span className="text-[10px] md:text-xs font-black opacity-90 uppercase tracking-tighter text-white/90">{dayData.turno}</span>
            )}

            {/* Cyan dot for jornada registered (not shown for vacation/quincena days) */}
            {dayData.jornada && !dayData.isVacacion && !dayData.isQuincena && (
                <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-cyan-400 absolute bottom-0.5 left-1 md:bottom-1.5 md:left-2" />
            )}

            {/* Dynamic Note Icon based on type */}
            {dayData.notas && dayData.notas.length > 0 && (() => {
                const hasMedico = dayData.notas.some(n => n.tipo === 'medico');
                const hasLaboral = dayData.notas.some(n => n.tipo === 'laboral');
                const hasPersonal = dayData.notas.some(n => n.tipo === 'personal');
                
                let Icon = Notebook;
                let colorClass = 'text-white/40 fill-white/10';
                let glowClass = '';

                if (hasMedico) {
                    Icon = Heart;
                    colorClass = 'text-red-400 fill-red-400/20';
                    glowClass = 'shadow-[0_0_10px_rgba(248,113,113,0.4)]';
                } else if (hasLaboral) {
                    Icon = Briefcase;
                    colorClass = 'text-amber-400 fill-amber-400/20';
                    glowClass = 'shadow-[0_0_10px_rgba(251,191,36,0.4)]';
                } else if (hasPersonal) {
                    Icon = Star;
                    colorClass = 'text-purple-400 fill-purple-400/20';
                    glowClass = 'shadow-[0_0_10px_rgba(168,85,247,0.4)]';
                } else {
                    Icon = MessageSquare;
                    colorClass = 'text-cyan-400 fill-cyan-400/20';
                    glowClass = 'shadow-[0_0_10px_rgba(34,211,238,0.4)]';
                }

                return (
                    <div className={`absolute bottom-0.5 right-0.5 md:bottom-1 md:right-1 z-20 p-1 rounded-md ${glowClass} bg-black/40 backdrop-blur-sm border border-white/10`}>
                        <Icon className={`w-3 h-3 md:w-4 md:h-4 ${colorClass}`} />
                        {dayData.notas.length > 1 && (
                            <div className="absolute -top-1 -left-1 w-3 h-3 bg-white text-[8px] font-black text-black rounded-full flex items-center justify-center border border-black/20">
                                {dayData.notas.length}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* AP Badge Indicator - Calendar style */}
            {apStatus && (
                <div className={`absolute top-0 right-0 md:top-0 md:right-0 z-20`}>
                    <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        className="md:w-8 md:h-8 drop-shadow-md"
                    >
                        {/* Calendar background */}
                        <rect
                            x="2" y="4" width="20" height="18" rx="3"
                            fill={apStatus.estado === 'pendiente' ? '#f59e0b' :
                                apStatus.estado === 'concedido' ? '#10b981' : '#ef4444'}
                        />
                        {/* Calendar top bar */}
                        <rect x="2" y="4" width="20" height="5" rx="3"
                            fill={apStatus.estado === 'pendiente' ? '#d97706' :
                                apStatus.estado === 'concedido' ? '#059669' : '#dc2626'}
                        />
                        {/* Calendar rings */}
                        <circle cx="7" cy="4" r="1.5" fill="white" opacity="0.9" />
                        <circle cx="17" cy="4" r="1.5" fill="white" opacity="0.9" />
                        {/* AP Text */}
                        <text x="12" y="17" textAnchor="middle" fontSize="8" fontWeight="bold" fill="white">
                            AP
                        </text>
                        {/* Status indicator */}
                        {apStatus.estado === 'concedido' && (
                            <circle cx="19" cy="19" r="4" fill="#059669" stroke="white" strokeWidth="1" />
                        )}
                        {apStatus.estado === 'concedido' && (
                            <path d="M17 19 L18.5 20.5 L21 17.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        )}
                        {apStatus.estado === 'rechazado' && (
                            <circle cx="19" cy="19" r="4" fill="#dc2626" stroke="white" strokeWidth="1" />
                        )}
                        {apStatus.estado === 'rechazado' && (
                            <path d="M17.5 17.5 L20.5 20.5 M20.5 17.5 L17.5 20.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        )}
                    </svg>
                </div>
            )}
            {/* Micro-icono para motivos sindicales parciales */}
            {(dayData.motivo?.tipo_hora_sindical === 'reunion_sindical' || 
              dayData.motivo?.tipo_hora_sindical === 'asamblea' || 
              (dayData.motivo?.motivo_nombre || "").toUpperCase().includes('REUNIÓN') || 
              (dayData.motivo?.motivo_nombre || "").toUpperCase().includes('ASAMBLEA')) && (
                <div className="absolute left-1 md:left-1.5 top-1/2 -translate-y-1/2 z-20 opacity-30 pointer-events-none">
                    <svg viewBox="0 0 24 24" className="w-3 h-3 md:w-3.5 md:h-3.5 text-white fill-white">
                        <path d="M19,13V8a2,2,0,0,0-2-2H16V5a2,2,0,0,0-2-2H11V2a1,1,0,0,0-2,0V3H7A2,2,0,0,0,5,5v8s0,1,1,1H7a1,1,0,0,0,1-1V9H9v4s0,1,1,1h1a1,1,0,0,0,1-1V10h1v3s0,1,1,1h1a1,1,0,0,0,1-1V11h1v2s0,1,1,1h1a1,1,0,0,0,1-1Z" />
                        <path d="M17,15H7a3,3,0,0,0-3,3v1a2,2,0,0,0,2,2H18a2,2,0,0,0,2-2V18A3,3,0,0,0,17,15Z" />
                    </svg>
                </div>
            )}
        </motion.button>
    );
};

export default memo(CalendarDay);
